import { defineComponent } from "vue";
import { CdrButton, IconSearch } from "@rei/cedar";
import GenericMedia from "../../GenericMedia/GenericMedia.mjs";
import UgcImage from "../../UgcImage/UgcImage.mjs";
import ClientOnly from "../../ClientOnly/ClientOnly.mjs";
import { animateCSS } from "../../../util/animate.mjs";
import swipeHandler from "../../../util/swipe.mjs";
import "js-cookie";
import { EImageType } from "../../../types/shared.mjs";
import StaffTestimonialAlternate from "../../StaffTestimonialAlternate/StaffTestimonialAlternate.mjs";
const _sfc_main = defineComponent({
  name: "MediaCenterHeroImage",
  components: {
    CdrButton,
    GenericMedia,
    IconSearch,
    ClientOnly,
    UgcImage,
    StaffTestimonialAlternate
  },
  props: {
    currentImage: {
      type: Object,
      required: true
    },
    images: {
      type: Array,
      required: true
    },
    layout: {
      type: String,
      default: "horizontal"
    },
    sizeParameter: {
      type: String,
      required: true
    },
    useJpgExt: {
      type: Boolean,
      default: false
    },
    videoLinkName: {
      type: String,
      required: true
    },
    hasFocusableImage: {
      type: Boolean,
      default: false
    },
    verticalAnimation: {
      type: Boolean,
      default: false
    },
    staffTestimonialProps: {
      type: Object,
      default: void 0
    }
  },
  emits: ["heroClick", "imageSelected", "metricsClick", "ugcImageClick", "imageSwipe"],
  data() {
    return {
      // derive actual image to display from prop, but hold intermediate state here to animate
      currentDisplayImage: this.currentImage,
      // reference to the cleanup function returned by the swipe hanlder
      cleanupSwipeHandler: null,
      resetVideo: true
    };
  },
  computed: {
    computedCurrentIndex() {
      return this.images.findIndex((elem) => elem.id === this.currentDisplayImage.id);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    computedPrev() {
      const {
        images,
        currentDisplayImage,
        computedCurrentIndex
      } = this;
      if (!currentDisplayImage.uri) return {};
      return computedCurrentIndex > 0 ? images[computedCurrentIndex - 1] : {};
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    computedNext() {
      const {
        images,
        currentDisplayImage,
        computedCurrentIndex
      } = this;
      if (!currentDisplayImage.uri) return {};
      return images[computedCurrentIndex + 1] ? images[computedCurrentIndex + 1] : {};
    },
    isVideo() {
      return this.currentDisplayImage.type === "video";
    },
    isUgcImage() {
      return this.currentDisplayImage.type === "UGC Image";
    },
    isStaffTestimonial() {
      return this.currentDisplayImage.type === EImageType.TESTIMONIAL;
    },
    isStandardImage() {
      return !this.isVideo && !this.isUgcImage;
    },
    componentType() {
      if (this.isStaffTestimonial) return StaffTestimonialAlternate;
      return this.isUgcImage ? UgcImage : GenericMedia;
    },
    genericMediaProps() {
      return {
        imageCaption: this.currentDisplayImage.caption,
        resetVideoOverride: this.resetVideo,
        videoLinkName: this.videoLinkName,
        videoSrc: this.currentDisplayImage.src,
        description: this.currentDisplayImage.longDescription
      };
    },
    ugcImageProps() {
      return {
        dataColor: this.currentImage.color && this.currentImage.color.name,
        imageSrc: this.currentDisplayImage.uri,
        title: this.currentDisplayImage.altText,
        products: this.currentDisplayImage.products,
        user: this.currentDisplayImage.user,
        likes: this.currentDisplayImage.likes,
        postedDate: this.currentDisplayImage.postedDate,
        layout: this.layout,
        currentProductId: this.currentImage.currentProductId
      };
    },
    getStaffTestimonialProps() {
      var _a, _b, _c, _d, _e, _f, _g;
      return {
        image: (_a = this.staffTestimonialProps) == null ? void 0 : _a.image,
        name: (_b = this.staffTestimonialProps) == null ? void 0 : _b.name,
        state: (_c = this.staffTestimonialProps) == null ? void 0 : _c.state,
        activity: (_d = this.staffTestimonialProps) == null ? void 0 : _d.activity,
        year: (_e = this.staffTestimonialProps) == null ? void 0 : _e.year,
        staffImg: (_f = this.staffTestimonialProps) == null ? void 0 : _f.staffImg,
        text: (_g = this.staffTestimonialProps) == null ? void 0 : _g.text
      };
    }
  },
  watch: {
    // imperative logic to sequence animation should be limited to this watch fn
    currentImage(newImage, oldImage) {
      if (newImage.id !== oldImage.id) {
        const oldPosition = this.images.findIndex((elem) => elem.id === oldImage.id);
        const newPosition = this.images.findIndex((elem) => elem.id === newImage.id);
        const isNewImageCollection = oldPosition === -1;
        const direction = oldPosition < newPosition ? "forward" : "backward";
        this.currentDisplayImage = newImage;
        if (!isNewImageCollection) {
          this.handleAnimate(direction);
        }
        this.resetVideo = true;
      }
    }
  },
  mounted() {
    this.cleanupSwipeHandler = swipeHandler(this.$refs.mainContainer, this.handleHeroSwipe, false, this.handleTouchMove);
  },
  beforeUnmount() {
    if (this.cleanupSwipeHandler) this.cleanupSwipeHandler();
  },
  methods: {
    handleAnimate(direction, animationFn = animateCSS) {
      const {
        mainContainer,
        prevContainer,
        nextContainer,
        currentContainer
      } = this.$refs;
      if (!prevContainer) {
        return;
      }
      let animateFrom;
      if (direction === "backward") {
        animateFrom = ((prevContainer == null ? void 0 : prevContainer.offsetLeft) ?? 0) - currentContainer.offsetLeft + mainContainer.offsetLeft;
      }
      if (direction === "forward") {
        animateFrom = ((nextContainer == null ? void 0 : nextContainer.offsetLeft) ?? 0) - currentContainer.offsetLeft + mainContainer.offsetLeft;
      }
      if (direction === "return") animateFrom = mainContainer.offsetLeft;
      mainContainer.style.left = `${animateFrom}px`;
      const opts = {
        animateFrom,
        animateTo: 0
      };
      animationFn(mainContainer, opts);
    },
    handleTouchMove(delta) {
      this.$refs.mainContainer.style.left = `${delta}px`;
    },
    handleHeroSwipe(direction) {
      if (direction === "swipeLeft") {
        if (this.computedNext.uri) {
          this.$emit("imageSwipe");
          this.$emit("imageSelected", {
            image: this.computedNext,
            // on swipe left, +1 for the next index, +1 for human-readable numbering = 2
            caption: `${this.computedCurrentIndex + 2} of ${this.images.length}`
          });
        } else this.handleAnimate("return");
      }
      if (direction === "swipeRight") {
        if (this.computedPrev.uri) {
          this.$emit("imageSwipe");
          this.$emit("imageSelected", {
            image: this.computedPrev,
            // on swipe right, -1 for the previous index, +1 for human-readable numbering = 0
            caption: `${this.computedCurrentIndex} of ${this.images.length}`
          });
        } else this.handleAnimate("return");
      }
      if (direction === "none") {
        this.handleAnimate("return");
      }
    },
    handleHeroClick() {
      const el = this.hasFocusableImage ? this.$refs.currentContainer : this.$refs.imageZoomButton;
      if (!this.isUgcImage) this.$emit("heroClick", el);
    },
    handleResetVideo(shouldReset) {
      if (typeof shouldReset === "boolean") this.resetVideo = shouldReset;
    },
    getComponentType(image) {
      if (image.type === EImageType.TESTIMONIAL) return StaffTestimonialAlternate;
      return image.type === "UGC Image" ? UgcImage : GenericMedia;
    },
    getShouldPad(image) {
      return image.type !== EImageType.ON_BODY && image.type !== EImageType.SWATCH && image.type !== EImageType.UGC && image.type !== EImageType.TESTIMONIAL;
    }
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=MediaCenterHeroImage2.mjs.map
